/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { ClipLoader } from 'react-spinners';

import { BsPersonPlus } from 'react-icons/bs';
import { ImBlocked } from 'react-icons/im';
import { RiMapPin2Line } from 'react-icons/ri';

import { BiX } from 'react-icons/bi';
import { useToast } from '../../../../hooks/toast';
import { useAuth } from '../../../../hooks/auth';

import api from '../../../../services/api';

import { calcOrderPrice } from '../../../../utils/calcOrderPrice';
import { formateBrPrice } from '../../../../utils/formateBrPrice';

import Button from '../../../../newComponents/Button';
import Select, { SelectOptionType } from '../../../../newComponents/Select';

import CartItem from '../CartItem';

import * as S from './styles';

import { ICustomer } from '../../../../interfaces/ICustomer';
import { IDeliveryAddress } from '../../../../interfaces/IDeliveryAddress';
import { ModalCreateAddress } from '../ModalCreateAddress';
import empresaApi from '../../../../services/empresaApi';
import { ICartItem } from '../../../../interfaces/ICartItem';
import { IDiscountCoupon } from '../../../../interfaces/IDiscountCoupon';
import { IFormaPagamentoEmpresa } from '../../../../interfaces/IPaymentMethod';
import CreateUserModal from '../ModalCreateUser';
import { ModalHandles } from '../../../../newComponents/Modal';

interface AsideProps {
  cart: ICartItem[];
  cartIsLoading?: boolean;
  selectedClient: ICustomer | null;
  selectedDeliveryType: 'TAKEOUT' | 'INDOOR' | 'DELIVERY';
  selectedAddress: IDeliveryAddress | null;
  selectedCoupon: IDiscountCoupon | null;
  deliveryFee: any;
  finalPrice: number;
  selectdePaymentMethod: {
    paymentMethod: IFormaPagamentoEmpresa;
    changeMoney: number | null;
  } | null;
  setSelectedClient?: React.Dispatch<React.SetStateAction<ICustomer | null>>;
  removeItemToCart: (id: number) => void;
  setSelectedAddress?: (value: IDeliveryAddress | null) => void;
  setSeletedEditCartItemId: (
    value: React.SetStateAction<number | null>,
  ) => void;
  setDeliveryType: (
    value: React.SetStateAction<'TAKEOUT' | 'INDOOR' | 'DELIVERY'>,
  ) => void;
  setModalCancelIsOpen: () => void;
  nextStep: () => void;
  discountOnTheOutside: number | null;
  step: number;
  selectedCompanyId: number;
  handleFinalizeOrder: () => Promise<void>;
  setAsideisClosed: React.Dispatch<React.SetStateAction<boolean>>;
  modalCreateUserRef?: React.RefObject<ModalHandles>;
}

const Aside: React.FC<AsideProps> = (props) => {
  const {
    cart,
    cartIsLoading,
    selectedCompanyId,
    selectedClient,
    selectedDeliveryType,
    selectedAddress,
    deliveryFee,
    selectedCoupon,
    finalPrice,
    discountOnTheOutside,
    step,
    selectdePaymentMethod,
    setModalCancelIsOpen,
    nextStep,
    setSelectedAddress,
    setSelectedClient,
    removeItemToCart,
    setSeletedEditCartItemId,
    setDeliveryType,
    handleFinalizeOrder,
    setAsideisClosed,
    modalCreateUserRef,
  } = props;

  const { addToast } = useToast();
  const { user } = useAuth();
  const isFirstRender = useRef(true);

  const [customers, setCustomers] = useState<ICustomer[]>([]);
  const [addresses, setAddresses] = useState<IDeliveryAddress[]>([]);

  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);

  const [modalCreateAddresIsOpen, setModalCreateAddressIsOpen] = useState(
    false,
  );

  const [indoorIsActive, setIndoorIsActive] = useState(false);

  const handleSelectCustomer = useCallback(
    (option: SelectOptionType | null): void => {
      if (!setSelectedClient) return;

      const customer = option
        ? customers.find((c) => c.id === option?.value)
        : null;
      setSelectedClient(customer ?? null);
    },
    [customers, setSelectedClient],
  );

  const customNextStep = (): void => {
    nextStep();
  };

  const addressInText = useCallback((address: any): string => {
    let res = '';

    if (address.logradouro) {
      res += `${address.logradouro}`;
    }

    if (address.numero) {
      res += `, nº ${address.numero}`;
    }

    if (address.complemento) {
      res += `, ${address.complemento}`;
    }

    if (address.bairro) {
      res += ` - ${address.bairro}.`;
    }

    if (address.cep) {
      res += ` CEP: ${address.cep}.`;
    }

    if (address.cidade) {
      res += ` ${address.cidade}`;
    }

    if (address.estado) {
      res += ` - ${address.estado}.`;
    }

    return res;
  }, []);

  const loadClients = useCallback(
    async (value: string): Promise<void> => {
      if (value.length < 3) return;

      setIsLoadingCustomers(true);
      try {
        const { data } = await api.get(
          `companies/${selectedCompanyId}/customers?`,
          {
            params: { search: value },
          },
        );
        setCustomers(data);
        setIsLoadingCustomers(false);
      } catch (error) {
        console.log(error);
        setIsLoadingCustomers(false);
      }
    },
    [selectedCompanyId],
  );

  const debouncedChangeClientsHandler = useCallback(
    debounce(loadClients, 1000),
    [],
  );

  const loadAddress = useCallback(async (): Promise<void> => {
    if (!selectedClient || !setSelectedAddress) return;

    setIsLoadingAddresses(true);
    try {
      const { data } = await api.get(
        `companies/${user.empresa_id}/delivery-addresses/customers/${selectedClient.id}`,
      );

      setAddresses(data);
      setIsLoadingAddresses(false);
    } catch (error) {
      console.log(error);
      setIsLoadingAddresses(false);
      if (
        (error as any)?.response.status === 404 &&
        (error as any)?.response.data.message === 'Nenhum endereço encontrado.'
      ) {
        addToast({
          type: 'error',
          title: 'Cliente não possui endereço cadastrado',
        });
        return;
      }

      addToast({
        type: 'error',
        title: 'Erro ao buscar endereços do cliente.',
      });
    }
  }, [addToast, selectedClient, setSelectedAddress, user.empresa_id]);

  useEffect(() => {
    setAddresses([]);

    if (selectedClient && selectedDeliveryType === 'DELIVERY') loadAddress();

    if (!isFirstRender.current) {
      setSelectedAddress && setSelectedAddress(null);
      return;
    }

    isFirstRender.current = false;
  }, [loadAddress, selectedClient, selectedDeliveryType, setSelectedAddress]);

  useEffect(() => {
    const indoorParams = async (): Promise<void> => {
      try {
        const res = await empresaApi.getParametro(
          selectedCompanyId,
          'consumo_local',
        );
        if (res) setIndoorIsActive(JSON.parse(res.role).status);
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Configurações de consumo no local não encontrado.',
        });
      }
    };

    indoorParams();
  }, [addToast, selectedCompanyId]);

  return (
    <S.Container>
      <div className="group g1">
        <button
          className="prev"
          type="button"
          onClick={() => setAsideisClosed(true)}
        >
          <BiX />
        </button>

        {setSelectedClient && (
          <div className="client">
            <div className="header">
              <label htmlFor="client">Cliente</label>
              {isLoadingCustomers && (
                <ClipLoader className="loading" size={18} />
              )}
            </div>

            <div className="client__search">
              <Select
                isClearable
                isError
                name="client"
                placeholder="Digite o nome ou telefone"
                value={
                  selectedClient
                    ? {
                        value: selectedClient.id,
                        label: `${selectedClient.tradingName ?? ''}-${
                          selectedClient.phoneNumber ?? ''
                        }`,
                      }
                    : null
                }
                options={customers.map((customer) => ({
                  value: customer.id,
                  label: `${customer.tradingName} - ${customer.phoneNumber}`,
                }))}
                onInputChange={(value: string) => {
                  debouncedChangeClientsHandler(value);
                }}
                onChange={handleSelectCustomer}
              />

              <Button onClick={() => modalCreateUserRef?.current?.open()}>
                <BsPersonPlus size={18} />
              </Button>
            </div>
          </div>
        )}

        <div className="cart">
          <h2>Carrinho</h2>
          <div>
            {cartIsLoading ? (
              <div className="loading">
                <ClipLoader size={18} />
              </div>
            ) : cart.length ? (
              cart
                .filter((item) => !item.deletedAt)
                .reverse()
                .map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    removeItemToCart={() => removeItemToCart(item.id)}
                    editItem={() => setSeletedEditCartItemId(item.id)}
                  />
                ))
            ) : (
              <p>Nenhum item</p>
            )}
          </div>
        </div>

        <div className="address">
          <div className="delivery__type">
            <div className="types">
              <S.ContainerItemSelectable
                seleted={selectedDeliveryType === 'TAKEOUT'}
                onClick={() => setDeliveryType('TAKEOUT')}
              >
                <p>Retirada</p>
              </S.ContainerItemSelectable>
              <S.ContainerItemSelectable
                title={
                  !indoorIsActive
                    ? 'Configuração de consumo no local está desabilitado.'
                    : undefined
                }
                disabled={!indoorIsActive}
                seleted={selectedDeliveryType === 'INDOOR'}
                onClick={() => {
                  indoorIsActive && setDeliveryType('INDOOR');
                }}
              >
                <p>Consumo no local</p>
              </S.ContainerItemSelectable>
              {!setSelectedClient &&
              selectedDeliveryType !== 'DELIVERY' ? null : (
                <S.ContainerItemSelectable
                  seleted={selectedDeliveryType === 'DELIVERY'}
                  onClick={() => setDeliveryType('DELIVERY')}
                >
                  <p>Entrega</p>
                </S.ContainerItemSelectable>
              )}
            </div>
          </div>

          {selectedDeliveryType === 'DELIVERY' ? (
            <div className="search__address">
              <div className="header">
                <label htmlFor="address">Endereço de entrega</label>
                {isLoadingAddresses && (
                  <ClipLoader className="loading" size={18} />
                )}
              </div>
              <div>
                {setSelectedAddress ? (
                  <>
                    <Select
                      placeholder="Selecione um endereço"
                      menuPlacement="top"
                      name="address"
                      value={
                        selectedAddress
                          ? {
                              value: selectedAddress.id,
                              label: addressInText(selectedAddress.address),
                            }
                          : null
                      }
                      options={addresses.map((address) => ({
                        value: address.id,
                        label: addressInText(address.address),
                      }))}
                      onChange={(option: SelectOptionType) => {
                        setSelectedAddress(
                          addresses.find(
                            (address) => option.value === address.id,
                          ) || null,
                        );
                      }}
                    />
                    <Button
                      onClick={() => {
                        if (selectedClient) {
                          setModalCreateAddressIsOpen(true);
                          return;
                        }
                        addToast({
                          type: 'info',
                          title: 'Selecione um cliente.',
                        });
                      }}
                    >
                      <RiMapPin2Line size={18} />
                    </Button>
                  </>
                ) : (
                  <p>
                    {selectedAddress
                      ? addressInText(selectedAddress.address)
                      : 'Nenhum endereço'}
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="group">
        <div className="prices">
          <div>
            <p>Subtotal:</p>
            <p>
              {formateBrPrice(
                cart
                  .filter((item) => item.deletedAt === undefined)
                  .map((item) => calcOrderPrice(item))
                  .reduce((acc: number, cur: number) => {
                    return Number(acc) + Number(cur);
                  }, 0),
              )}
            </p>
          </div>
          {deliveryFee ? (
            <div>
              <p>Taxa de entrega:</p>
              <p>{formateBrPrice(Number(deliveryFee.value))}</p>
            </div>
          ) : null}
          {deliveryFee && !!deliveryFee.discount ? (
            <div>
              <p>Desconto na taxa de entrega:</p>
              <p>- {formateBrPrice(Number(deliveryFee.discount))}</p>
            </div>
          ) : null}
          {selectedCoupon ? (
            <div>
              <p>{`Cupom: ${selectedCoupon.code || ''}`}</p>
              <p>
                -
                {selectedCoupon?.type === 1
                  ? formateBrPrice(+selectedCoupon.value)
                  : formateBrPrice(
                      cart
                        .filter((item) => item.deletedAt === undefined)
                        .map((item) => calcOrderPrice(item))
                        .reduce((acc: number, cur: number) => {
                          return acc + +cur;
                        }, 0) *
                        (+selectedCoupon.value / 100),
                    )}
              </p>
            </div>
          ) : null}
          {discountOnTheOutside ? (
            <div>
              <p>Desconto:</p>
              <p>{`-${formateBrPrice(discountOnTheOutside)}`}</p>
            </div>
          ) : null}
          <div>
            <p>
              <strong>Valor total:</strong>
            </p>
            <p>
              <strong>{formateBrPrice(finalPrice)}</strong>
            </p>
          </div>
          {selectdePaymentMethod?.changeMoney ? (
            <div>
              <p>Troco:</p>
              <p>
                {formateBrPrice(selectdePaymentMethod.changeMoney - finalPrice)}
              </p>
            </div>
          ) : null}
        </div>

        <div className="conclusion">
          <Button
            onClick={() =>
              step === 0 ? customNextStep() : handleFinalizeOrder()
            }
            disabled={
              isLoadingCustomers ||
              isLoadingAddresses ||
              (!!selectedAddress && !deliveryFee)
            }
          >
            {step === 0 ? 'Avançar' : 'Finalizar'}
          </Button>
          <Button onClick={setModalCancelIsOpen}>
            <ImBlocked size={20} />
          </Button>
        </div>
      </div>

      {modalCreateAddresIsOpen ? (
        <ModalCreateAddress
          isOpen
          setModalCreateAddressIsOpen={setModalCreateAddressIsOpen}
          selectedClient={selectedClient as ICustomer}
          setAddresses={setAddresses}
        />
      ) : null}

      {setSelectedClient ? (
        <CreateUserModal
          modalRef={modalCreateUserRef}
          setCustomers={setCustomers}
          setSelectedClient={setSelectedClient}
        />
      ) : null}
    </S.Container>
  );
};

export default Aside;
